/**
 * Game: the browser shell around Simulation.
 *
 * Owns everything that needs a DOM or a GPU - renderer, camera, quality tiers,
 * VFX, audio, HUD, input devices, and the RAF loop. It drives the simulation
 * with a fixed timestep and subscribes to presentation events the simulation
 * emits ('vfx:pop', 'camera:shake') without the simulation ever knowing a
 * renderer exists.
 *
 * Keep it that way. Any gameplay logic added here instead of in simulation.ts
 * becomes untestable the moment it is written.
 */

import * as THREE from 'three';
import { GameLoop } from '@/core/loop';
import { tuning } from '@/core/tuning';
import { vec3 } from '@/core/math';
import { InputManager, KeyboardMouseSource } from '@/engine/input';
import { initPhysics } from '@/engine/physicsWorld';
import { placeholderCount, preloadReadyAssets } from '@/engine/assetRegistry';
import { AudioManager } from '@/engine/audio';
import {
  applyQualitySettings,
  createRenderer,
  followSunTarget,
  handleResize,
  type RendererBundle,
} from '@/engine/renderer';
import { FollowCamera } from '@/engine/camera';
import { QualityManager } from '@/render/qualityTiers';
import { applyOutlineQuality, countOutlines } from '@/render/outline';
import { VfxSystem } from '@/render/vfx';
import { InstinctView } from '@/render/instinctView';
import { instinctTimeScale, selectInstinctMarks, type InstinctCandidate } from '@/systems/instinct';
import { Hud } from '@/ui/hud';
import { ActivityChip, placeChip } from '@/ui/activityChip';
import { LevelCard } from '@/ui/levelCard';
import { LevelEditor } from '@/ui/levelEditor';
import { saveLevelDraft } from '@/editor/levelSaveClient';
import type { EditorTarget } from '@/editor/levelEditorModel';
import { createSaveStore, type SaveStore } from '@/engine/storage';
import {
  bestFor,
  recordAttempt,
  recordResult,
  type LevelRecord,
  type SaveData,
} from '@/systems/save';
import { Simulation } from './simulation';

export type { ObjectiveProgress, GamePhase } from './simulation';

interface EditorViewSnapshot {
  object: THREE.Object3D;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
}

export class Game {
  private readonly sim: Simulation;
  private readonly render: RendererBundle;
  private readonly quality: QualityManager;
  private readonly vfx: VfxSystem;
  private readonly camera: FollowCamera;
  private readonly input: InputManager;
  private readonly audio: AudioManager;
  private readonly hud: Hud;
  private readonly activityChip: ActivityChip;
  private readonly levelCard: LevelCard;
  private readonly levelEditor: LevelEditor;
  private readonly editorMarker: THREE.Box3Helper;
  private readonly loop: GameLoop;
  private readonly instinct: InstinctView;
  private readonly onResize: () => void;

  private readonly saves: SaveStore;
  private save: SaveData;
  /** The saved best from BEFORE this run, so the card can say "new best". */
  private readonly previousBest: LevelRecord | null;
  /** Set once the win has been folded into the save, so it happens exactly once. */
  private resultRecorded = false;

  /** Reused so holding Instinct does not allocate an array every frame. */
  private readonly instinctCandidates: InstinctCandidate[] = [];

  /* Scratch vectors for the activity chip's projection, reused every frame. */
  private readonly chipPoint = new THREE.Vector3();
  private readonly cameraForward = new THREE.Vector3();
  private readonly toGranny = new THREE.Vector3();
  private readonly editorViewSnapshots = new Map<string, EditorViewSnapshot>();

  private constructor(
    canvas: HTMLCanvasElement,
    levelData: unknown,
    saves: SaveStore,
    levelFileName: string,
  ) {
    this.quality = new QualityManager(canvas);
    this.render = createRenderer(canvas, this.quality.settings);

    this.sim = new Simulation({ scene: this.render.scene, levelData });

    this.vfx = new VfxSystem(this.render.scene);
    this.vfx.setDensity(this.quality.settings.vfxDensity);

    this.instinct = new InstinctView(this.render.scene);

    this.editorMarker = new THREE.Box3Helper(new THREE.Box3(), tuning.editor.markerColor);
    this.editorMarker.name = 'editor:selection';
    this.editorMarker.visible = false;
    const markerMaterial = this.editorMarker.material;
    if (!Array.isArray(markerMaterial)) {
      markerMaterial.depthTest = false;
      markerMaterial.depthWrite = false;
    }
    this.editorMarker.renderOrder = 10;
    this.render.scene.add(this.editorMarker);

    this.camera = new FollowCamera(this.render.camera, this.sim.physics);
    // Open looking into the room rather than at whichever wall the bed is against.
    this.camera.faceTowards(this.sim.cat.headPosition, this.sim.roomCenter);
    this.camera.reset(this.sim.cat.headPosition);

    this.input = new InputManager(0).add(new KeyboardMouseSource(), canvas);

    this.audio = new AudioManager();
    this.audio.attach(this.sim.bus);

    const uiRoot = document.getElementById('ui-root') as HTMLElement;
    this.hud = new Hud(uiRoot);
    // After the HUD: its constructor replaces the root's innerHTML, which would
    // otherwise throw these away the moment they were created.
    this.activityChip = new ActivityChip(uiRoot);
    this.levelCard = new LevelCard(uiRoot, {
      onPlayAgain: () => this.replayLevel(),
      onLevels: () => this.returnToLevelSelect(),
    });
    this.levelEditor = new LevelEditor(uiRoot, {
      level: this.sim.level.def,
      fileName: levelFileName,
      canSaveToDisk: import.meta.env.DEV,
      onOpen: (targets) => this.beginEditorPreview(targets),
      onPreview: (target) => this.previewEditorTarget(target),
      onClose: () => this.restoreEditorViews(),
      onSave: saveLevelDraft,
      onReload: () => window.location.reload(),
    });

    // Read the save before the first frame, and count the attempt immediately -
    // a child who quits halfway still played, and "plays" is what a future level
    // select uses to offer help on a level someone keeps bouncing off.
    this.saves = saves;
    this.save = this.saves.load();
    this.previousBest = bestFor(this.save, this.sim.level.def.id);
    this.save = recordAttempt(this.save, this.sim.level.def.id);
    this.saves.save(this.save);

    this.loop = new GameLoop({
      onFixedStep: (dt) => this.fixedStep(dt),
      onRender: (alpha, realDt) => this.renderFrame(alpha, realDt),
    });

    this.wirePresentation();

    this.onResize = () => handleResize(this.render);
    window.addEventListener('resize', this.onResize);
    this.quality.onChange((_, settings) => {
      applyQualitySettings(this.render, settings);
      this.vfx.setDensity(settings.vfxDensity);
      applyOutlineQuality(this.render.scene, settings.outlines);
    });

    // Apply the initial tier's outline setting too, not just later changes -
    // a device that boots straight into 'low' must get the cheaper scene.
    applyOutlineQuality(this.render.scene, this.quality.settings.outlines);
  }

  static async create(
    canvas: HTMLCanvasElement,
    levelData: unknown,
    saves: SaveStore = createSaveStore(),
    levelFileName = 'level.json',
  ): Promise<Game> {
    await initPhysics();
    await preloadReadyAssets();
    return new Game(canvas, levelData, saves, levelFileName);
  }

  start(): void {
    this.sim.bus.emit('level:loaded', { levelId: this.sim.level.def.id });
    this.loop.start();
  }

  stop(): void {
    this.loop.stop();
    this.input.detachAll();
    this.audio.detach();
    window.removeEventListener('resize', this.onResize);
    this.vfx.clear();
    this.instinct.dispose();
    this.activityChip.dispose();
    this.levelCard.dispose();
    this.levelEditor.dispose();
    this.render.scene.remove(this.editorMarker);
    this.editorMarker.geometry.dispose();
    const markerMaterials = Array.isArray(this.editorMarker.material)
      ? this.editorMarker.material
      : [this.editorMarker.material];
    for (const material of markerMaterials) material.dispose();
    this.sim.dispose();
  }

  /**
   * Restarts the level from the card.
   *
   * A reload rather than an in-place teardown, deliberately. Rebuilding the
   * simulation means unwinding a Rapier world, a scene graph and a level's
   * worth of colliders, and a leak there shows up as a mystery frame-rate
   * collapse three plays later. Progress is already on disk by this point, and
   * the level is five minutes long. Revisit when there is a level SELECT to
   * return to rather than a single level to repeat.
   */
  private replayLevel(): void {
    window.location.reload();
  }

  /** Returns to the pre-physics selector through a clean page teardown. */
  private returnToLevelSelect(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('level');
    window.location.assign(url.toString());
  }

  /** Folds a finished level into the save. Runs exactly once per win. */
  private recordWin(): void {
    if (this.resultRecorded) return;
    const scored = this.sim.scored;
    if (!scored) return;
    this.resultRecorded = true;
    this.save = recordResult(this.save, scored);
    this.saves.save(this.save);
  }

  /** Translates simulation events into sound, particles and camera motion. */
  private wirePresentation(): void {
    const bus = this.sim.bus;

    bus.on('vfx:pop', ({ position, text, color }) => this.vfx.pop(position, text, color));
    bus.on('vfx:dust', ({ position, amount }) => this.vfx.dust(position, amount));
    bus.on('camera:shake', ({ amount }) => this.camera.addShake(amount));

    bus.on('cat:sprayed', ({ position }) => {
      this.vfx.splash(vec3(position.x, position.y + 0.3, position.z), vec3(0, 0.3, 0), 14);
      this.audio.playAt('spray_hiss', position);
    });

    bus.on('cat:spotted', () => this.audio.playUi('granny_gasp'));
    bus.on('cat:caught', () => this.audio.playUi('level_fail'));
    bus.on('key:collected', () => this.audio.playUi('key_collect'));
    bus.on('objective:complete', () => this.audio.playUi('objective_done'));
    bus.on('catMoment:completed', () => this.audio.playUi('cozy_moment'));
    bus.on('level:won', () => this.audio.playUi('level_win'));

    // The camera resets after the catch cutaway so it does not whip across the
    // room following the teleport.
    bus.on('cat:recovered', () => this.camera.reset(this.sim.cat.headPosition));
  }

  private fixedStep(dt: number): void {
    const input = this.input.sampleForStep();

    if (input.justPressed.has('toggleEditor')) {
      if (this.sim.phase === 'playing' || this.levelEditor.isOpen) this.levelEditor.toggle();
    }

    // Editor previews deliberately move render objects without their colliders.
    // Freezing gameplay is what keeps that temporary split from becoming a lie.
    if (this.levelEditor.isOpen) return;

    // Camera look first, so movement this step is relative to where the player
    // is now looking rather than one step behind.
    this.camera.applyLook(input.lookDeltaX, input.lookDeltaY);
    this.sim.step(dt, input, this.camera.getMovementBasis());
  }

  /**
   * Everything Cat Instinct could mark this frame.
   *
   * Reads gameplay state and never writes it - this runs in the render frame, so
   * a mutation here would be a variable-rate change to the simulation.
   */
  private collectInstinctCandidates(): InstinctCandidate[] {
    const candidates = this.instinctCandidates;
    candidates.length = 0;

    for (const prop of this.sim.props.props) {
      candidates.push({
        id: prop.id,
        category: 'prop',
        position: prop.object.position,
        eligible: !prop.broken && !prop.carried && (prop.mischief !== undefined || prop.carryable),
      });
    }

    for (const key of this.sim.level.keys) {
      if (!key.position) continue;
      candidates.push({
        id: key.id,
        category: 'key',
        position: key.position,
        eligible: key.available && !key.collected,
      });
    }

    for (const trace of this.sim.traces.traces) {
      candidates.push({
        id: trace.id,
        category: 'trace',
        position: trace.position,
        eligible: !trace.resolved,
      });
    }

    // Hidden and quiet is the only -3/s heat decay in the game, so when things go
    // wrong an escape route is the most useful thing a child can be shown. Until
    // now every hide spot was an invisible trigger volume.
    for (const spot of this.sim.level.hideSpots) {
      candidates.push({
        id: spot.id,
        category: 'hideSpot',
        position: spot.position,
        eligible: true,
        occupied: this.sim.hidingIn === spot.id,
      });
    }

    // "Take the yarn to your bed" named a destination the game never drew, so a
    // player could pick the yarn up and have no idea what to do with it. The
    // objective text is no help to a six-year-old who cannot read it.
    for (const objective of this.sim.objectives) {
      const zone = objective.def.dropZone;
      if (!zone) continue;
      candidates.push({
        id: `goal:${objective.def.id}`,
        category: 'goal',
        position: vec3(zone.position[0], zone.position[1], zone.position[2]),
        eligible: !objective.complete,
      });
    }

    return candidates;
  }

  private updateInstinct(realDt: number): void {
    // `lastFrame` is the read-only view of the most recent fixed step's input, so
    // the render frame can see the held state without draining the sources - only
    // fixedStep may call sampleForStep().
    const held = this.input.lastFrame.down.has('instinct');
    const playing = this.sim.phase === 'playing' && !this.levelEditor.isOpen;
    const active = held && playing;

    // Slow motion enters through the accumulator, never through a system's `dt`.
    this.loop.setTimeScale(instinctTimeScale({ held, playing }));

    this.instinct.setActive(active);
    this.sim.granny.setConeSeeThrough(active);
    if (!active) return;

    this.instinct.update(
      selectInstinctMarks(this.collectInstinctCandidates(), this.sim.cat.position),
      realDt,
    );
  }

  /** Projects granny's head to screen space and parks the activity chip there. */
  private updateActivityChip(): void {
    if (this.sim.phase !== 'playing' || this.levelEditor.isOpen) {
      this.activityChip.hide();
      return;
    }

    const eye = this.sim.granny.eyePosition;
    this.chipPoint.set(eye.x, eye.y + tuning.granny.presentation.chipHeadClearance, eye.z);

    const camera = this.render.camera;
    const distance = this.chipPoint.distanceTo(camera.position);
    // Projection alone cannot tell "in front" from "directly behind" - both land
    // inside the NDC box - so ask the camera which way it is facing first.
    camera.getWorldDirection(this.cameraForward);
    this.toGranny.subVectors(this.chipPoint, camera.position);
    const behindCamera = this.toGranny.dot(this.cameraForward) <= 0;

    this.chipPoint.project(camera);
    const canvas = this.render.renderer.domElement;

    this.activityChip.update(
      this.sim.granny.presentation,
      placeChip({
        ndc: { x: this.chipPoint.x, y: this.chipPoint.y },
        behindCamera,
        distance,
        width: canvas.clientWidth,
        height: canvas.clientHeight,
      }),
    );
  }

  /** Shows the results card once the level is won, and banks the result. */
  private updateLevelCard(): void {
    const scored = this.sim.scored;
    if (this.sim.phase !== 'won' || !scored) {
      this.levelCard.hide();
      return;
    }
    this.recordWin();
    this.levelCard.show({
      levelName: this.sim.level.def.name,
      scored,
      previousBest: this.previousBest,
      saved: this.saves.writable,
    });
  }

  private renderFrame(_alpha: number, realDt: number): void {
    this.quality.sample(realDt);

    this.sim.cat.updateView(realDt);
    this.updateInstinct(realDt);
    this.updateActivityChip();
    this.updateLevelCard();

    const focus = this.sim.cat.headPosition;
    this.camera.setFrenzy(this.sim.heat.tier === 'frenzy' ? 1 : 0);
    this.camera.update(focus, this.sim.cat.velocity, realDt);

    followSunTarget(this.render.sun, new THREE.Vector3(focus.x, focus.y, focus.z));
    this.audio.setListener(focus);

    // Keep the carried prop glued to the cat's mouth.
    const carried = this.sim.carrying;
    if (carried && !this.levelEditor.isOpen) {
      const cat = this.sim.cat;
      const forward = vec3(-Math.sin(cat.yaw), 0, -Math.cos(cat.yaw));
      carried.object.position.set(
        cat.position.x + forward.x * 0.22,
        cat.position.y + tuning.cat.height * 0.55,
        cat.position.z + forward.z * 0.22,
      );
    }

    this.vfx.update(realDt);

    this.hud.update({
      heat: this.sim.heat.heat,
      tier: this.sim.heat.tier,
      contact: this.sim.heat.contact,
      grannyState: this.sim.granny.machine.current,
      objectives: this.sim.objectives,
      keysCollected: this.sim.keysCollected,
      keysRequired: this.sim.level.def.keysRequired ?? 2,
      chaos: this.sim.chaosScore,
      cozy: this.sim.cozyPoints,
      hiding: this.sim.hidingIn !== null,
      hidingSeen: this.sim.hideEntrySeen,
      dashCooldown: this.sim.cat.dashCooldownRemaining / tuning.cat.dash.cooldown,
      pounceCharge: this.sim.cat.pounceChargeRatio,
      carrying: carried?.id ?? null,
      phase: this.sim.phase,
      exhausted: this.sim.granny.exhausted,
      debug: {
        fps: this.loop.fps,
        p95: this.loop.p95FrameTime * 1000,
        quality: this.quality.current,
        placeholders: placeholderCount(),
        propCount: this.sim.props.props.length,
        particles: this.vfx.particleCount,
        drawCalls: this.render.renderer.info.render.calls,
        triangles: this.render.renderer.info.render.triangles,
        outlines: countOutlines(this.render.scene),
      },
    });

    this.render.renderer.render(this.render.scene, this.render.camera);
  }

  /** Exposed for the Playwright smoke test and console poking. */
  get state() {
    return { ...this.sim.snapshot, fps: this.loop.fps, p95Ms: this.loop.p95FrameTime * 1000 };
  }

  /**
   * Read-only Cat Instinct state, for the Playwright assertions.
   *
   * Deliberately a snapshot of derived values rather than the view or the loop:
   * a test that can reach in and mutate simulation internals stops being a test
   * of the shipped game.
   */
  get instinctState() {
    return {
      active: this.instinct.isActive,
      timeScale: this.loop.currentTimeScale,
      stepCount: this.loop.stepCount,
      marks: { ...this.instinct.markCounts },
    };
  }

  /** Read-only editor state for browser assertions and console inspection. */
  get editorState() {
    const target = this.levelEditor.selectedTarget;
    return {
      active: this.levelEditor.isOpen,
      dirty: this.levelEditor.isDirty,
      elapsedSeconds: this.sim.elapsedSeconds,
      selectedKey: target?.key ?? null,
      selectedPosition: target ? [...target.position] : null,
    };
  }

  private beginEditorPreview(targets: readonly EditorTarget[]): void {
    this.editorViewSnapshots.clear();
    for (const target of targets) {
      const object = this.editorObjectFor(target);
      if (!object) continue;
      this.editorViewSnapshots.set(target.key, {
        object,
        position: object.position.clone(),
        quaternion: object.quaternion.clone(),
      });
    }
    // Start from one coherent authored draft, even if dynamic props had moved
    // during play or this draft was saved and reopened before its required reload.
    for (const target of targets) this.applyEditorTarget(target);
  }

  private previewEditorTarget(target: EditorTarget): void {
    const object = this.applyEditorTarget(target);
    this.editorMarker.box.makeEmpty();
    if (object) this.editorMarker.box.setFromObject(object);

    if (this.editorMarker.box.isEmpty()) {
      const center = new THREE.Vector3(...target.position);
      const size = new THREE.Vector3().setScalar(tuning.editor.markerSize);
      this.editorMarker.box.setFromCenterAndSize(center, size);
    }
    this.editorMarker.visible = true;
  }

  private restoreEditorViews(): void {
    for (const snapshot of this.editorViewSnapshots.values()) {
      snapshot.object.position.copy(snapshot.position);
      snapshot.object.quaternion.copy(snapshot.quaternion);
      snapshot.object.updateMatrixWorld(true);
    }
    this.editorViewSnapshots.clear();
    this.editorMarker.visible = false;
  }

  private applyEditorTarget(target: EditorTarget): THREE.Object3D | null {
    const object = this.editorObjectFor(target);
    if (!object) return null;
    object.position.set(target.position[0], target.position[1], target.position[2]);
    if (target.rotationY !== undefined) object.rotation.y = target.rotationY;
    object.updateMatrixWorld(true);
    return object;
  }

  private editorObjectFor(target: EditorTarget): THREE.Object3D | null {
    if (target.kind === 'geometry') {
      return this.render.scene.getObjectByName(`geo:${target.id}`) ?? null;
    }
    if (target.kind === 'prop') {
      return this.render.scene.getObjectByName(`prop:${target.id}`) ?? null;
    }
    if (target.kind === 'key') {
      return this.render.scene.getObjectByName(`key:${target.id}`) ?? null;
    }
    if (target.kind === 'grannySpawn') return this.sim.granny.object;
    return null;
  }
}
